import { useEffect, useState, useContext } from "react";
import { Stack, Box, Paper, Typography, InputLabel, Button, FormControlLabel, Switch, Alert } from "@mui/material";
import TitlePage from "@components/atoms/TitlePage/TitlePage";
import TitlePaper from "@components/atoms/TitlePaper/TitlePaper";
import SearchableSelect from "@components/atoms/SearchableSelect/SearchableSelect";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import DynamicTable from "../DynamicTable/DynamicTable";
import CreateReceiptTop from "@components/molecules/CreateReceiptTop/CreateReceiptTop";
import MessageBar from "@components/atoms/MessageBar/MessageBar";
import { clientService } from "@services/ClientService";
import { receiptService } from "@services/ReceiptService";
import { grey } from "@mui/material/colors";
import { createHeader, getCurrency, getNumberCurrency } from "@utils/utils";
import { IClient, IClientSummary, INewProduct, INewReceipt } from "src/types/global";
import { Controller, useForm } from "react-hook-form";
import { useFormValidate } from "@utils/hooks";
import ErrorAlert from "@components/atoms/ErrorAlert/ErrorAlert";
import { useNavigate } from "react-router-dom";
import { GlobalAlertContext } from "@providers/GlobalAlertProvider";
import InputText from "@components/atoms/InputText/InputText";
import ConfirmDialog from "@components/molecules/ConfirmDialog/ConfirmDialog";
interface IFormValues extends INewReceipt {
  employeeCode: string;
  applicant: string;
}
const CreateReceipt = () => {
  const [products, setProducts] = useState<INewProduct[]>([]);
  const [rows, setRows] = useState<INewProduct[]>([]);
  const [pages, setPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [productError, setProductError] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [clientSummary, setClientSummary] = useState<IClientSummary | null>(null);
  const rowsPerPage = 6;
  const {
    register,
    control,
    trigger,
    getValues,
    reset,
    watch,
    formState: {
      errors
    }
  } = useForm<IFormValues>({
    defaultValues: {
      receiptType: false,
      employeeCode: "",
      applicant: ""
    }
  });
  const {
    validateField
  } = useFormValidate();
  const selectedItem = watch("item");
  useEffect(() => {
    if (!selectedItem || selectedItem === "-1") {
      setClientSummary(null);
      return;
    }
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const client: IClient = JSON.parse(selectedItem);
        if (!client?.id) return;
        const res = await clientService.getSummary(client.id);
        if (!cancelled) setClientSummary(res.data);
      } catch {
        if (!cancelled) setClientSummary(null);
      }
    };
    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [selectedItem]);
  const {
    setGlobalAlertMessage,
    setGlobalAlertSeverity,
    showGlobalAlert
  } = useContext(GlobalAlertContext);
  const navigate = useNavigate();
  const getTotalValue = () => getCurrency(products.map(p => p.totalPrice).reduce((total, currentValue) => {
    const currentTotal = currentValue ? parseFloat(currentValue.replaceAll(".", "")) : 0;
    return total + currentTotal;
  }, 0));
  const onSubmit = async (data: IFormValues) => {
    if (products.length === 0) {
      return setProductError(true);
    }
    try {
      setIsLoading(true);
      setGlobalAlertSeverity("success");
      setGlobalAlertMessage("!Listo! El recibo ha sido creado correctamente");
      const client: IClient = JSON.parse(data.item || "");
      data.clientId = client.id;
      const dateJs = data.dateJs;
      data.limitDate = dateJs ? dateJs.format("YYYY-MM-DD") : "";
      delete data.dateJs;
      delete data.item;
      data.products = [...products.map(prod => {
        const localProd = {
          ...prod
        };
        localProd.price = getNumberCurrency(String(localProd.price));
        delete localProd.totalPrice;
        delete localProd.actions;
        return {
          ...localProd
        };
      })];
      data.receiptType = data.receiptType ? 1 : 0;
      const res = await receiptService.create(data);
      navigate(`/recibos/${res.data.id}`, {
        state: {
          showAlert: true
        }
      });
      showGlobalAlert();
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
      handleCloseDialog();
    }
  };
  const handleShowDialog = async () => {
    const isValid = await trigger(["name", "item", "dateJs"]);
    if (isValid) {
      if (products.length === 0) {
        return setProductError(true);
      }
      setShowEmployeeDialog(true);
    }
  };
  const handleCloseDialog = () => {
    setShowEmployeeDialog(false);
    reset({
      ...getValues(),
      employeeCode: "",
      applicant: ""
    });
  };
  useEffect(() => {
    setRows([...products.slice(rowsPerPage * (currentPage - 1), rowsPerPage * currentPage)]);
  }, [products, currentPage]);
  useEffect(() => {
    setPages(Math.ceil(products.length / rowsPerPage));
  }, [products]);
  const classes = {
    mainPaper: {
      width: 1,
      padding: "24px",
      height: "fit-content"
    },
    inputLabel: {
      color: grey[900],
      marginBottom: 0.5
    },
    transacPaper: {
      width: 400,
      padding: "24px",
      height: "fit-content"
    },
    switch: {
      margin: 0,
      display: "flex",
      justifyContent: "space-between"
    }
  };
  const headers = [createHeader("Producto", "productName"), createHeader("Cantidad", "quantity"), createHeader("Precio unitario", "price"), createHeader("Total", "totalPrice"), createHeader("Acciones", "actions")];
  return <>
      <TitlePage title={"Crear recibo"} subtitle="Inicia un recibo de caja nuevo con un cliente y sus productos" />
      <Stack direction={"row"} marginTop={3} spacing={3} alignItems={"flex-start"}>
        <Stack width={1}>
          <Paper elevation={12} sx={classes.mainPaper}>
            <TitlePaper title="Información del recibo" subtitle="Completa los campos del cliente y fecha de vencimiento antes de continuar." />
            <Stack component={"form"} noValidate direction={"row"} justifyContent={"space-between"} alignItems={"flex-start"} marginTop={3} flexWrap={"wrap"}>
              <Box marginBottom={2} width={1}>
                <InputText label="Nombre" name="name" placeholder="Ingresa un nombre para el recibo" register={register("name")} disabled={isLoading} />
              </Box>
              <Box marginBottom={2} width={0.49}>
                <SearchableSelect label="Cliente" placeholder="Selecciona cliente" loading="Cargando clientes" errorMessage="Error obteniendo clientes" primaryKey="fullName" secondaryKey="identification" query={(rowsPerPage, search) => clientService.getAll(rowsPerPage, undefined, search)} register={register("item", {
                required: true,
                validate: value => value !== "-1"
              })} helperText={validateField(errors.item)} disabled={isLoading} />
              </Box>
              {clientSummary?.isOverCap && <Box width={1} marginBottom={2}>
                  <Alert severity="warning">
                    Este cliente supera su cupo. Consumido:{" "}
                    {getCurrency(clientSummary.consumedAmount)} / Cupo:{" "}
                    {getCurrency(clientSummary.moneyCapAmount ?? 0)}. Podés
                    continuar creando el recibo.
                  </Alert>
                </Box>}
              <Box marginBottom={2} width={0.49}>
                <InputLabel htmlFor="dateJs" sx={classes.inputLabel}>
                  Fecha límite de pago
                </InputLabel>
                <Controller name="dateJs" control={control} rules={{
                required: true
              }} render={({
                field: {
                  ref,
                  onChange
                }
              }) => <DatePicker sx={{
                width: 1
              }} minDate={dayjs()} inputRef={ref} onChange={onChange} slotProps={{
                textField: {
                  helperText: validateField(errors?.dateJs)
                }
              }} disabled={isLoading} />} />
              </Box>
            </Stack>
          </Paper>
          <DynamicTable title="Productos" subtitle="Agrega los productos necesarios para completar el recibo." headers={headers} rows={rows} pages={pages} onChangePage={setCurrentPage} emptyMessage="Aún no hay productos agregados" topRender={<CreateReceiptTop setProducts={setProducts} isLoading={isLoading} />} bottomRender={<MessageBar label="Total a pagar" value={getTotalValue()} />} />
        </Stack>
        <Paper elevation={12} sx={classes.transacPaper}>
          <TitlePaper title="Resumen" />
          <FormControlLabel sx={classes.switch} control={<Switch defaultChecked={false} defaultValue={0} disabled={isLoading} {...register("receiptType")} />} label="Devoluciones" labelPlacement="start" />
          <Stack direction={"row"} justifyContent={"space-between"} marginBottom={1.5}>
            <Typography color={grey[600]}>Productos</Typography>
            <Typography color={grey[600]}>
              {products.map(p => p.quantity).reduce((total, current) => total + Number(current), 0)}
            </Typography>
          </Stack>
          <MessageBar size="small" label="Total" value={getTotalValue()} />
          <Button fullWidth onClick={handleShowDialog} disabled={isLoading}>
            Crear recibo
          </Button>
        </Paper>
      </Stack>
      <ConfirmDialog open={showEmployeeDialog} title="Confirmar recibo" subtitle="Ingresa tu código de empleado *" message="Por favor, Ingresa tu código de empleado * para confirmar la creación del recibo." cancelLabel="Cancelar" confirmLabel="Confirmar" cancelAction={handleCloseDialog} confirmAction={() => onSubmit(getValues())} contentRender={<Box marginTop={1}>
            <InputText label="Código de empleado" name="employeeCode" placeholder="Ingresa tu código de empleado *" register={register("employeeCode", {
        required: true
      })} disabled={isLoading} sx={{
        mb: 2
      }} autoFocus={true} type="password" />
            <InputText label="Solicitante" name="applicant" placeholder="Ingresa el nombre del solicitante" register={register("applicant")} disabled={isLoading} sx={{
        mb: 2
      }} />
          </Box>} disabled={!watch("employeeCode")?.trim() || isLoading} withEnter={true} />
      <ErrorAlert open={productError} onClose={() => setProductError(false)} message={"Error, debes agregar por lo menos un producto"} duration={3000} />
      <ErrorAlert open={showError} onClose={() => setShowError(false)} message={"Lo sentimos, no se pudo crear este recibo, intentalo nuevamente"} duration={3000} />
    </>;
};
export default CreateReceipt;